// scheduling/components/pool/ClaimConfirmModal.jsx
// Confirm gate for taking a job out of a pool (Job Pools, Trello #257).
//
// There is no un-claim in v1 — claiming writes to PE (work status + forward +
// preparer role), so backing it out would mean a second set of PE writes. Hence an
// explicit confirm rather than a one-click claim.
//
// Used for BOTH self-claim and manager-assign; `assignee` switches the copy.

import { POOL_META } from '../../utils/poolEligibility';

const fmtHours = (h) => (h == null ? '0' : String(Math.round(Number(h) * 100) / 100));

export default function ClaimConfirmModal({ job, assignee, onConfirm, onClose, submitting, error }) {
  const isAssign = !!assignee;
  const poolLabel = POOL_META[job.PoolCode]?.label || job.PoolCode;

  return (
    <div className="sched-mgr-modal-backdrop" onClick={submitting ? undefined : onClose}>
      <div className="sched-mgr-modal" onClick={e => e.stopPropagation()}>
        <div className="sched-mgr-modal-header">
          <h3>{isAssign ? 'Assign this job' : 'Claim this job'}</h3>
          {!submitting && (
            <button className="sched-mgr-sidebar-close" onClick={onClose} aria-label="Close">&times;</button>
          )}
        </div>

        <div className="sched-mgr-modal-job">
          <span className="sched-mgr-modal-job-name">{job.JobName}</span>
          <span className="sched-mgr-modal-job-client">{job.ClientName}</span>
          <span className="sched-mgr-modal-job-budget">
            {job.ServiceDesc || job.JobType || 'General'} · {fmtHours(job.BudgetHours)}h budget · {poolLabel} pool
          </span>
        </div>

        {job.Notes && (
          <div className="sched-mgr-modal-field">
            <label className="sched-mgr-modal-label">Notes from {job.PooledByName || 'the manager'}</label>
            <div className="sched-pool-card-notes" style={{ margin: 0 }}>&ldquo;{job.Notes}&rdquo;</div>
          </div>
        )}

        <div className="sched-pool-confirm-what">
          <div className="sched-pool-confirm-head">
            {isAssign ? `${assignee.Name} will be given this job:` : 'Claiming this will:'}
          </div>
          <ul>
            <li>
              Set {isAssign ? `${assignee.Name} as` : 'you as'} the job&rsquo;s current staff and
              primary preparer in Practice Engine
            </li>
            <li>Move the PE work status to Preparation</li>
            <li>
              {isAssign
                ? `Notify ${assignee.Name} in RPRS, and forward the job to them in PE`
                : 'Take it out of the pool so nobody else can claim it'}
            </li>
            {isAssign && <li>Clear their availability signal, if they had one set</li>}
          </ul>
          <div className="sched-pool-confirm-note">
            Tracking continues in PE from here — RPRS hands the job over and steps out
            of the way. There is no undo.
          </div>
        </div>

        {error && <div className="sched-pool-error">{error}</div>}

        <div className="sched-pool-actions">
          <button
            type="button"
            className="sched-mgr-modal-btn sched-pool-btn-cancel"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="sched-mgr-modal-btn sched-pool-btn-confirm"
            onClick={onConfirm}
            disabled={submitting}
          >
            {submitting
              ? (isAssign ? 'Assigning…' : 'Claiming…')
              : (isAssign ? `Assign to ${assignee.Name.split(' ')[0]}` : 'Claim it')}
          </button>
        </div>
      </div>
    </div>
  );
}
